import React, { useEffect, useState, useRef } from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import ImageUpload from '../components/ImageUpload';
import { listDocs, updateDocById, deleteDocById } from '../services/dbService';
import toast from 'react-hot-toast';
import './Events.css';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', price: '', imageUrl: '' });
  const imageUploadRef = useRef(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const docs = await listDocs('services');
      setServices(
        docs.map((d) => ({
          id: d.id,
          name: d.name || '',
          description: d.description || '',
          price: typeof d.price === 'number' ? d.price : parseFloat(d.price || 0) || 0,
          imageUrl: d.imageUrl || d.image_url || null,
          createdAt: d.created_at || d.createdAt || null,
          status: d.status || 'pending', // default to pending if missing
        }))
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  // Edit handler for services
  const handleEdit = (service) => {
    setEditing(service);
    setFormData({
      name: service.name,
      description: service.description,
      price: service.price,
      imageUrl: service.imageUrl || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({ name: '', description: '', price: '', imageUrl: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (imageUploadRef.current && imageUploadRef.current.hasFile && imageUploadRef.current.hasFile()) {
        const uploadedUrl = await imageUploadRef.current.uploadIfPending();
        if (uploadedUrl) formData.imageUrl = uploadedUrl;
      }
      
      if (editing) {
        await updateDocById('services', editing.id, {
          name: formData.name,
          description: formData.description,
          price: parseFloat(formData.price || 0),
          imageUrl: formData.imageUrl
        });
        toast.success('Service updated successfully');
      }
      handleCloseModal();
      fetchServices();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update service');
    }
  };

  // Status update handlers
  const handleVerify = async (id) => {
    try {
      await updateDocById('services', id, { status: 'verified' });
      toast.success('Service verified');
      fetchServices();
    } catch (err) {
      console.error(err);
      toast.error('Failed to verify service');
    }
  };

  const handleReject = async (id) => {
    try {
      await updateDocById('services', id, { status: 'rejected' });
      toast.success('Service rejected');
      fetchServices();
    } catch (err) {
      console.error(err);
      toast.error('Failed to reject service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await deleteDocById('services', id);
      toast.success('Service deleted');
      fetchServices();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete service');
    }
  };

  // (Removed duplicate handleDelete declaration)

  if (loading) return <div className="events-container"><p>Loading...</p></div>;

  // Categorize services
  const pendingServices = services.filter((s) => s.status === 'pending');
  const verifiedServices = services.filter((s) => s.status === 'verified');
  const rejectedServices = services.filter((s) => s.status === 'rejected');

  return (
    <div className="events-container animate-fade-in">
      <div className="events-header">
        <h1>Services</h1>
      </div>

      {/* Horizontal Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #eee', marginBottom: 24 }}>
        <button
          className={activeTab === 'pending' ? 'tab-active' : 'tab'}
          style={{
            flex: 1,
            padding: '12px 0',
            border: 'none',
            borderBottom: activeTab === 'pending' ? '2px solid #007bff' : '2px solid transparent',
            background: 'none',
            fontWeight: activeTab === 'pending' ? 'bold' : 'normal',
            color: activeTab === 'pending' ? '#007bff' : '#333',
            cursor: 'pointer',
            outline: 'none',
            transition: 'border-bottom 0.2s',
          }}
          onClick={() => setActiveTab('pending')}
        >
          Pending
        </button>
        <button
          className={activeTab === 'verified' ? 'tab-active' : 'tab'}
          style={{
            flex: 1,
            padding: '12px 0',
            border: 'none',
            borderBottom: activeTab === 'verified' ? '2px solid #28a745' : '2px solid transparent',
            background: 'none',
            fontWeight: activeTab === 'verified' ? 'bold' : 'normal',
            color: activeTab === 'verified' ? '#28a745' : '#333',
            cursor: 'pointer',
            outline: 'none',
            transition: 'border-bottom 0.2s',
          }}
          onClick={() => setActiveTab('verified')}
        >
          Verified
        </button>
        <button
          className={activeTab === 'rejected' ? 'tab-active' : 'tab'}
          style={{
            flex: 1,
            padding: '12px 0',
            border: 'none',
            borderBottom: activeTab === 'rejected' ? '2px solid #dc3545' : '2px solid transparent',
            background: 'none',
            fontWeight: activeTab === 'rejected' ? 'bold' : 'normal',
            color: activeTab === 'rejected' ? '#dc3545' : '#333',
            cursor: 'pointer',
            outline: 'none',
            transition: 'border-bottom 0.2s',
          }}
          onClick={() => setActiveTab('rejected')}
        >
          Rejected
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'pending' && (
        <div className="events-table-wrapper">
          <table className="events-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Description</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingServices.length > 0 ? (
                pendingServices.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.price ? `${s.price} ETB` : '—'}</td>
                    <td>{s.description}</td>
                    <td>{s.imageUrl ? <img src={s.imageUrl} alt="img" style={{ width: 80, height: 50, objectFit: 'cover' }} /> : 'N/A'}</td>
                    <td className="action-buttons">
                      <button className="btn btn-sm btn-success" onClick={() => handleVerify(s.id)}>
                        Verify
                      </button>
                      <button className="btn btn-sm btn-warning" onClick={() => handleReject(s.id)}>
                        Reject
                      </button>
                      <button className="btn btn-sm btn-info" onClick={() => handleEdit(s)}>
                        <FiEdit2 /> Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(s.id)}>
                        <FiTrash2 /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">No pending services</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'verified' && (
        <div className="events-table-wrapper">
          <table className="events-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Description</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {verifiedServices.length > 0 ? (
                verifiedServices.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.price ? `${s.price} ETB` : '—'}</td>
                    <td>{s.description}</td>
                    <td>{s.imageUrl ? <img src={s.imageUrl} alt="img" style={{ width: 80, height: 50, objectFit: 'cover' }} /> : 'N/A'}</td>
                    <td className="action-buttons">
                      <button className="btn btn-sm btn-warning" onClick={() => handleReject(s.id)}>
                        Reject
                      </button>
                      <button className="btn btn-sm btn-info" onClick={() => handleEdit(s)}>
                        <FiEdit2 /> Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(s.id)}>
                        <FiTrash2 /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">No verified services</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'rejected' && (
        <div className="events-table-wrapper">
          <table className="events-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Description</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rejectedServices.length > 0 ? (
                rejectedServices.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.price ? `${s.price} ETB` : '—'}</td>
                    <td>{s.description}</td>
                    <td>{s.imageUrl ? <img src={s.imageUrl} alt="img" style={{ width: 80, height: 50, objectFit: 'cover' }} /> : 'N/A'}</td>
                    <td className="action-buttons">
                      <button className="btn btn-sm btn-success" onClick={() => handleVerify(s.id)}>
                        Verify
                      </button>
                      <button className="btn btn-sm btn-info" onClick={() => handleEdit(s)}>
                        <FiEdit2 /> Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(s.id)}>
                        <FiTrash2 /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">No rejected services</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Service</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="event-form">
              <div className="form-group">
                <label>Service Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  required
                />
              </div>
              <div className="form-group">
                <label>Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Image</label>
                <ImageUpload
                  ref={imageUploadRef}
                  initialUrl={formData.imageUrl}
                  autoUpload={true}
                  path={`services/${editing ? editing.id : 'services'}`}
                  onUpload={(url) => setFormData((prev) => ({ ...prev, imageUrl: url }))}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Update Service
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;